import { useEffect, useMemo } from 'react';
import { CrowCopilot } from '@usecrow/ui';
import { useLocation, useNavigate } from 'react-router-dom';
import { useContextHook } from 'use-context-hook';
import authService from '../services/authService';
import { AuthContext } from '../Context/authContext';

const CROW_API_URL = process.env.REACT_APP_CROW_API_URL || 'https://api.usecrow.org';
const PRODUCT_ID = process.env.REACT_APP_CROW_PRODUCT_ID || 'user_3C5Psn1Md7008fUyX7xUnkh8BkP';

let crowTokenPromise = null;
async function getCrowIdentityToken() {
  if (crowTokenPromise) {
    return crowTokenPromise;
  }

  crowTokenPromise = authService
    .getCrowToken()
    .then((response) => response?.data?.token || response?.token || '')
    .catch(() => {
      crowTokenPromise = null;
      return '';
    });

  return crowTokenPromise;
}

export function CrowSetup() {
  const location = useLocation();
  const navigate = useNavigate();
  const { isLoggedIn, user } = useContextHook(AuthContext, ['isLoggedIn', 'user']);
  const isCrowEnabled = process.env.REACT_APP_CROW_ENABLED === 'true';
  const canRenderCrow = isCrowEnabled && location.pathname !== '/' && isLoggedIn;

  const userData = user?.data || user || {};
  const userName = user?.data?.fullName || user?.fullName || user?.data?.name || user?.name || 'there';
  const emailConnected = Boolean(userData?.emailExists);
  const linkedinConnected = Boolean(userData?.linkedinProfileExists);
  const campaignExists = Boolean(userData?.campaignExists);
  const onboardingCompleted = emailConnected && linkedinConnected && campaignExists;

  const registerCrowTools = tools => {
    if (window?.crow) {
      window.crow('registerTools', tools);
    }
  };

  const navigateResult = (path, extra = {}) => {
    navigate(path, extra.options || undefined);
    return { status: 'success', navigatedTo: path, ...(extra.payload || {}) };
  };

  const goToCampaignManager = () => navigateResult('/campaign-manager');

  const crowClientTools = useMemo(
    () => ({
      navigateToPage: async ({ page }) => navigateResult(page),
      navigate_to_page: async ({ page }) => navigateResult(page),
      startNewCampaign: async () => goToCampaignManager(),
      createCampaign: async () => goToCampaignManager(),
      create_campaign: async () => goToCampaignManager(),
      openCreateCampaign: async () => goToCampaignManager(),
      open_create_campaign: async () => goToCampaignManager(),
      goToCreateCampaign: async () => goToCampaignManager(),
      go_to_create_campaign: async () => goToCampaignManager(),
      campaignManager: async () => goToCampaignManager(),
      campaign_manager: async () => goToCampaignManager(),
      openCampaignAnalytics: async () => navigateResult('/campaign-analytics'),
      connectEmailAccount: async () => navigateResult('/connect-email-account'),
      connectLinkedinAccount: async () =>
        navigateResult('/email-accounts', {
          options: { state: { activeTab: 'linkedin' } },
          payload: { activeTab: 'linkedin' },
        }),
      /* Was '/import-jobs', which is parked — the copilot would have sent the
         user to a page that redirects straight back to AI Home. Contacts is the
         live leads screen, so the action still does what it says. */
      openFindLeads: async () => navigateResult('/leads-module'),
    }),
    [navigate],
  );

  useEffect(() => {
    if (!canRenderCrow) return;
    registerCrowTools(crowClientTools);
  }, [canRenderCrow, crowClientTools]);

  if (!canRenderCrow) return null;

  return (
    <CrowCopilot
      productId={PRODUCT_ID}
      apiUrl={CROW_API_URL}
      variant="floating"
      position="right"
      defaultOpen={false}
      width={400}
      fullscreenTopOffset={0}
      context={{
        name: userName,
        userName,
        emailConnected,
        linkedinConnected,
        campaignExists,
        onboardingCompleted,
      }}
      onReady={() => {
        registerCrowTools(crowClientTools);
      }}
      getIdentityToken={getCrowIdentityToken}
    />
  );
}

export default CrowSetup;
